/**
 * useRecommendations – Zustand store for recommendation management.
 */
import { create } from 'zustand'
import {
  getRecommendations,
  getPendingRecommendations,
  approveRecommendation as apiApprove,
  rejectRecommendation as apiReject,
} from '@/lib/api'
import type { Recommendation } from '@/lib/types'

type FilterTab = 'pending' | 'approved' | 'rejected' | 'all'

interface RecommendationsState {
  recommendations: Recommendation[]
  filter: FilterTab
  loading: boolean
  approving: number | null
  rejecting: number | null
  error: string | null
  // Actions
  setFilter: (filter: FilterTab) => void
  fetchRecommendations: () => Promise<void>
  approve: (id: number) => Promise<void>
  reject: (id: number, reason?: string) => Promise<void>
}

export const useRecommendations = create<RecommendationsState>()((set, get) => ({
  recommendations: [],
  filter: 'pending',
  loading: false,
  approving: null,
  rejecting: null,
  error: null,

  setFilter: (filter) => {
    set({ filter })
    get().fetchRecommendations()
  },

  fetchRecommendations: async () => {
    const { filter } = get()
    set({ loading: true, error: null })
    try {
      let data: Recommendation[]
      if (filter === 'pending') {
        data = await getPendingRecommendations()
      } else if (filter === 'all') {
        data = await getRecommendations()
      } else {
        data = await getRecommendations(filter)
      }
      set({ recommendations: data, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  approve: async (id) => {
    set({ approving: id, error: null })
    try {
      await apiApprove(id)
      set({ approving: null })
      await get().fetchRecommendations()
    } catch (e) {
      set({ error: String(e), approving: null })
    }
  },

  reject: async (id, reason) => {
    set({ rejecting: id, error: null })
    try {
      await apiReject(id, reason)
      set({ rejecting: null })
      await get().fetchRecommendations()
    } catch (e) {
      set({ error: String(e), rejecting: null })
    }
  },
}))
